import React from 'react';
import {
  Check, Clock, HandHeart, MapPin, Home, ExternalLink, Trash2, Moon,
} from 'lucide-react';
import { getCat } from './taskCategories';

const snoozeRemaining = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return null;
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 1) return `${hours}h`;
  const mins = Math.max(1, Math.floor(ms / 60_000));
  return `${mins}m`;
};

export const TaskCard = ({ task, userId, onClaim, onComplete, onUnclaim, onSnooze, onDelete, isOverdue }) => {
  const cat = getCat(task.category);
  const CatIcon = cat.icon;
  const isMyClaim = task.claimed_by === userId;
  const isMyTask = task.created_by === userId;
  const snoozeLeft = snoozeRemaining(task.snoozed_until);

  return (
    <div className={`surface-card rounded-xl p-4 ${isOverdue ? 'border-rose-300 dark:border-rose-700' : ''}`} data-testid={`task-${task.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg ${cat.color} flex items-center justify-center flex-shrink-0`}>
          <CatIcon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-foreground">{task.title}</p>
            {task.status === 'claimed' && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300 flex-shrink-0">
                {task.claimed_by_name}
              </span>
            )}
          </div>
          {task.description && <p className="text-xs text-muted-foreground mt-0.5">{task.description}</p>}

          {(task.pickup || task.dropoff) && (
            <div className="text-xs mt-1.5 space-y-0.5">
              {task.pickup && <p className="text-blue-600 flex items-center gap-1"><MapPin className="w-3 h-3" /> From: {task.pickup}</p>}
              {task.dropoff && <p className="text-blue-600 flex items-center gap-1"><MapPin className="w-3 h-3" /> To: {task.dropoff}</p>}
              {task.round_trip && <p className="text-blue-500 font-medium">↩ Round trip</p>}
            </div>
          )}
          {task.location && !task.pickup && (
            <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {task.location}
            </p>
          )}
          {task.address && (
            <p className="text-xs text-purple-600 mt-1 flex items-center gap-1">
              <Home className="w-3 h-3" /> {task.address}
            </p>
          )}
          {task.meal_info && (
            <p className="text-xs text-amber-600 mt-1">{task.meal_info}</p>
          )}
          {task.meal_train_link && (
            <a href={task.meal_train_link} target="_blank" rel="noopener noreferrer"
              className="text-xs text-cyan-600 hover:underline mt-1 inline-flex items-center gap-1">
              <ExternalLink className="w-3 h-3" /> Meal Train
            </a>
          )}

          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] text-muted-foreground">{task.creator_name}</span>
            {task.due_date && (
              <span className={`text-[10px] flex items-center gap-0.5 ${isOverdue ? 'text-rose-500 font-medium' : 'text-muted-foreground'}`}>
                <Clock className="w-3 h-3" />
                {new Date(task.due_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 pt-2 border-t border-border/30 flex flex-wrap items-center gap-x-3 gap-y-2">
        {task.status === 'open' && (
          <button
            onClick={() => onClaim(task)}
            className="text-xs font-medium text-cyan-600 hover:text-cyan-700 flex items-center gap-1 transition-colors"
            data-testid={`claim-task-${task.id}`}
          >
            <HandHeart className="w-3.5 h-3.5" /> I{"'"}ll do this
          </button>
        )}
        {task.status === 'claimed' && isMyClaim && (
          <>
            <button
              onClick={() => onComplete(task.id)}
              className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 transition-colors"
              data-testid={`complete-task-${task.id}`}
            >
              <Check className="w-3.5 h-3.5" /> Mark done
            </button>
            {onSnooze && !snoozeLeft && (
              <button
                onClick={() => onSnooze(task.id)}
                className="text-xs font-medium text-indigo-600 hover:text-indigo-700 flex items-center gap-1 transition-colors"
                data-testid={`snooze-task-${task.id}`}
              >
                <Moon className="w-3.5 h-3.5" /> Snooze 24h
              </button>
            )}
            {snoozeLeft && (
              <span
                className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 flex items-center gap-1"
                data-testid={`snoozed-badge-${task.id}`}
                title="Auto-releases if not completed"
              >
                <Moon className="w-3 h-3" /> Snoozed · auto-releases in {snoozeLeft}
              </span>
            )}
            <button
              onClick={() => onUnclaim(task.id)}
              className="text-xs font-medium text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
              data-testid={`unclaim-task-${task.id}`}
            >
              Unclaim
            </button>
          </>
        )}
        {task.status === 'claimed' && isMyTask && !isMyClaim && (
          <button
            onClick={() => onComplete(task.id)}
            className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 transition-colors"
            data-testid={`complete-task-${task.id}`}
          >
            <Check className="w-3.5 h-3.5" /> Mark done
          </button>
        )}
        {isMyTask && (
          <button
            onClick={() => onDelete(task.id)}
            className="ml-auto text-xs text-muted-foreground/50 hover:text-rose-500 transition-colors"
            data-testid={`delete-task-${task.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};
